/**
 * Collapsing the duplicate location work a cold launch performs.
 *
 * Two independent effects mount in the same frame and both refresh the same
 * location-derived state: the tab layout's session load calls `refreshAll()`
 * (which includes venue and nearby), and Home's focus effect calls
 * `refreshVenue`, `refreshGroups` and `refreshNearby` on its own. Neither knows
 * about the other, so a cold launch acquires GPS twice, reads `venues/current`
 * twice, and writes presence twice before the first useful pixel.
 *
 * `createResyncGate` already solves the same shape of problem for resume and
 * reconnect, but it gates one whole `refreshAll`. This gates *per read*, because
 * the duplicate callers here overlap only partially — Home refreshes venue and
 * nearby, the layout refreshes those plus inbox, chats and groups.
 *
 * Two rules, and the second is the one that matters at launch:
 *
 * 1. A read that completed within `minIntervalMs` is not repeated.
 * 2. A read already **in flight** is not started again. At launch both callers
 *    fire in the same frame, so neither has finished when the other arrives —
 *    a completion-time check alone would let both through and change nothing.
 *
 * Skipping rather than awaiting is deliberate and matches `resync-gate`: the run
 * in flight was started after whatever prompted the second request, so it reads
 * the same server state. The caller's own `finally` still releases its spinner.
 */

export type RefreshKey = "venue" | "nearby";

export type RefreshOptions = {
  /**
   * Run regardless of recency or a run in flight. For work the user explicitly
   * asked for (pull-to-refresh) or that must observe a state change the gate
   * cannot see (returning from the system location settings with a new grant).
   */
  force?: boolean;
  /**
   * How old this read is allowed to be before the caller wants it repeated.
   * Widens rule 1 for one call; rule 2 (a read already in flight) still holds,
   * because two reads of the same thing at once is never what anybody wanted.
   *
   * The default window exists to collapse a launch, and it is short for that
   * reason. A caller with a STALENESS policy — "a list of people this old is
   * not worth showing" — has a different question and a different number, and
   * had no way to ask it: the alternatives were `force`, which re-reads on
   * every focus, and nothing, which re-reads on none. Home's people panel sat
   * on the second, so a row could outlive the person indefinitely.
   */
  maxAgeMs?: number;
};

export type LaunchRefresh = {
  /** Claim the right to run. Returns false when the caller should skip. */
  begin: (key: RefreshKey, options?: RefreshOptions) => boolean;
  /**
   * Release the claim. Always call, including on failure.
   *
   * `read: false` releases WITHOUT stamping freshness — the caller claimed the
   * right to read and then did not complete one, so the next caller must not
   * inherit a window it never earned. The default is `true` because almost
   * every release does follow a completed read, and a caller that has to
   * remember to say so is a caller that forgets.
   */
  end: (key: RefreshKey, outcome?: { read?: boolean }) => void;
};

type Options = {
  /**
   * How long after a completed read the same read is considered fresh. Long
   * enough to cover mount and first focus, which land in the same frame; short
   * enough that a user returning to Home a moment later still gets fresh data.
   */
  minIntervalMs?: number;
  /** Injectable for tests. */
  now?: () => number;
};

const DEFAULT_MIN_INTERVAL_MS = 3_000;

export function createLaunchRefresh(options: Options = {}): LaunchRefresh {
  const minIntervalMs = options.minIntervalMs ?? DEFAULT_MIN_INTERVAL_MS;
  const now = options.now ?? Date.now;

  const running = new Set<RefreshKey>();
  const finishedAt = new Map<RefreshKey, number>();

  return {
    begin(key, opts) {
      if (opts?.force) {
        running.add(key);
        return true;
      }
      if (running.has(key)) return false;
      const last = finishedAt.get(key);
      // A caller's own staleness bound wins over the launch-collapse default.
      // Never both: the wider of the two is not a policy, it is an accident.
      const window = opts?.maxAgeMs ?? minIntervalMs;
      if (last !== undefined && now() - last < window) return false;
      running.add(key);
      return true;
    },
    end(key, outcome) {
      running.delete(key);
      // A read that did not happen must not move the freshness clock. Stamping
      // it makes `begin` skip the NEXT caller inside the window, which is the
      // gate telling a screen its data is current on the strength of a request
      // that failed. Releasing the `running` claim is unconditional either way:
      // a caller that has stopped running has stopped running.
      if (outcome?.read === false) return;
      finishedAt.set(key, now());
    },
  };
}
